import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";

import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Eye, Users, ChevronDown, ChevronRight, Plus, Search, X, Loader2, AlertCircle, Zap, FileText, Trash2, UserMinus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationPageSize,
} from "@/components/ui/pagination";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from "sonner";
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/contexts/RoleAccessContext';
import { useTeamScope, usePerformanceBase } from '@/contexts/TeamScopeContext';
import { createCanHelpers } from '@/utils/rbac';

import { getAppraisals, getExitInterviews, getPIPs, cancelReview, deleteReview } from '@/services/performanceService';
import { getEmployees } from '@/services/adminService';
import { getTemplates } from '@/services/templateService';
import { Textarea } from '@/components/ui/textarea';
import PIPFormDialog from './PIPFormDialog';
import OneTimeReviewDialog from './OneTimeReviewDialog';
import AppraisalCycleDialog from './AppraisalCycleDialog';
import AssignExitInterviewDialog from './AssignExitInterviewDialog';

const STEPS = ["Self Review", "Approval Review", "Employee Comment", "Admin Review", "Completed"];
const EMPLOYEE_STEPS = ["Self Review", "Approval Review", "Employee Comment", "Admin Review", "Completed"];
const TAB_VALUES = ["appraisals", "pips", "exit-interviews"];
const AVAILABLE_TAB_VALUES = TAB_VALUES;

const stepIndex = (status, isEmployee = false) => {
  const steps = isEmployee ? EMPLOYEE_STEPS : STEPS;
  const i = steps.indexOf(status);
  return i >= 0 ? i : 0;
};

const StepTimeline = ({ currentStatus, isEmployee = false }) => {
  const displaySteps = (isEmployee ? EMPLOYEE_STEPS : STEPS).slice(0, 5);
  const currentIdx = stepIndex(currentStatus, isEmployee);

  return (
    <div className="flex items-center gap-0" data-testid="step-timeline">
      {displaySteps.map((step, i) => {
        // Determine if this step is completed
        const isCompleted = currentStatus === 'Completed' || (i < currentIdx);

        const isCurrent = i === currentIdx && currentStatus !== 'Completed';

        return (
          <div key={i} className="flex items-center">
            <div className="flex items-center gap-1.5 shrink-0">
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${isCompleted
                ? 'bg-primary border-primary text-white'
                : isCurrent
                  ? 'bg-primary/20 border-primary text-primary'
                  : 'bg-card border-border text-muted-foreground'
                }`}>
                {isCompleted && (currentStatus === 'Completed' || i < currentIdx) ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-xs whitespace-nowrap ${isCurrent ? 'text-primary font-semibold' : isCompleted ? 'text-primary font-medium' : 'text-muted-foreground'
                }`}>
                {step}
              </span>
            </div>
            {i < displaySteps.length - 1 && (
              <div className={`h-0.5 w-8 mx-1.5 shrink-0 ${isCompleted ? 'bg-primary' : 'bg-border'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const config = {
    "Pending": "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30",
    "Self Review": "bg-primary/10 text-primary border-primary/30 dark:bg-primary/20 dark:text-primary dark:border-primary/40",
    "Approval Review": "bg-primary/20 text-primary border-primary/40 dark:bg-primary/30 dark:border-primary/50",
    "Admin Review": "bg-primary/30 text-primary border-primary/50 dark:bg-primary/40 dark:border-primary/60",
    "Employee Comment": "bg-primary/40 text-primary border-primary/60 dark:bg-primary/50 dark:border-primary/70",
    "Submitted": "bg-primary/20 text-primary border-primary/40 dark:bg-primary/30 dark:border-primary/50",
    "Completed": "bg-primary text-primary-foreground border-primary dark:bg-primary dark:text-white dark:border-primary",
    "Cancelled": "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  };
  return <Badge variant="outline" className={`text-[10px] ${config[status] || "bg-muted border-muted-foreground/30 text-muted-foreground dark:border-muted-foreground/30"}`}>{status}</Badge>;
};

const MyAppraisalsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    AVAILABLE_TAB_VALUES.includes(tabParam) ? tabParam : "appraisals",
  );
  const [cycles, setCycles] = useState([]);
  const [appraisalsByCycle, setAppraisalsByCycle] = useState({});
  const [expandedCycleId, setExpandedCycleId] = useState(null); // Single cycle ID instead of object
  const [loading, setLoading] = useState(true);
  // Which performance action dialog is open, driven by the "Actions" dropdown.
  // One of: "oneTime" | "pip" | "cycle" | "exit" | null.
  const [perfAction, setPerfAction] = useState(null);
  const [pips, setPips] = useState([]);
  const [expandedPipId, setExpandedPipId] = useState(null); // Single PIP ID instead of object
  const [exitInterviews, setExitInterviews] = useState([]);
  const [expandedExitInterviewId, setExpandedExitInterviewId] = useState(null);

  // Consolidated filter state
  const [filters, setFilters] = useState({
    appraisals: { search: '', status: 'all', department: 'all', page: 1 },
    pips: { search: '', status: 'all', department: 'all', page: 1 },
    exitInterviews: { search: '', status: 'all', department: 'all', page: 1 }
  });

  const [pageSize, setPageSize] = useState(5);

  // Helper to update filters
  const updateFilter = (tab, key, value) => {
    setFilters(prev => ({
      ...prev,
      [tab]: { ...prev[tab], [key]: value, page: key === "page" ? value: 1 } // Reset page on filter change
    }));
  };

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    updateFilter('appraisals', 'page', 1);
    updateFilter('pips', 'page', 1);
    updateFilter('exitInterviews', 'page', 1);
  };

  const [cancelTargetReview, setCancelTargetReview] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const [deleteTargetReview, setDeleteTargetReview] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const MIN_CANCEL_REASON_LENGTH = 10;
  const normalizedCancelReason = cancelReason.trim();
  const isCancelReasonValid = normalizedCancelReason.length >= MIN_CANCEL_REASON_LENGTH;



  // Data
  const [employeeList, setEmployeeList] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const { user } = useAuth();
  // On "My team" pages this is a manager's view of their direct reports.
  const teamScope = useTeamScope();
  const basePath = usePerformanceBase();
  const isEmployeeView = user?.role === 'employee' && !teamScope;
  // Which tabs / assign options a team view offers follows the company's
  // manager duties; elsewhere everything shows.
  const showAppraisals = !teamScope || teamScope.duties.appraisals;
  const showPips = !teamScope || teamScope.duties.pips;

  const { can } = usePermission();
  // `can` is stable (memoized in the context on [permissions, user]), so the
  // helpers and derived flags only need to be computed when `can` changes —
  // not on every click-triggered re-render.
  const { canView, canCreate, canDelete, canApprove, canReview } = useMemo(
    () => createCanHelpers(can),
    [can]
  );

  const {
    canViewAppraisal,
    canCreateAppraisal,
    canViewPIP,
    canViewExitInterview,
    canCreatePerf,
    canDeletePerf,
  } = useMemo(
    () => ({
      canViewAppraisal: canReview("performance_management"),
      canCreateAppraisal: canCreate("performance_management"),
      canViewPIP: canReview("performance_management"),
      canViewExitInterview: canReview("performance_management"),
      canCreatePerf: canCreate("performance_management"),
      canDeletePerf: canDelete("performance_management"),
    }),
    [canReview, canCreate, canDelete]
  );

  // Use the memoized booleans (no per-row can() calls during render).
  const canCancelReviewRecord = (review) => {
    if (!canCreatePerf) return false;
    if (!review || !["Appraisal", "PIP", "Exit Interview"].includes(review.reviewType)) return false;
    return !["Completed", "Cancelled"].includes(review.status);
  };

  const canDeleteReviewRecord = (review) => {
    if (!canDeletePerf) return false;
    if (!review || !["Appraisal", "PIP", "Exit Interview"].includes(review.reviewType)) return false;
    return review.status === "Cancelled";
  };

  const openCancelDialog = (review) => {
    setCancelTargetReview(review);
    setCancelReason("");
  };

  useEffect(() => {
    const nextTab = AVAILABLE_TAB_VALUES.includes(tabParam)
      ? tabParam
      : "appraisals";
    // A team view without appraisals opens on PIPs.
    setActiveTab(nextTab !== "pips" && !showAppraisals && showPips ? "pips" : nextTab);
  }, [tabParam, showAppraisals, showPips]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === "appraisals") {
        next.delete("tab");
      } else {
        next.set("tab", value);
      }
      return next;
    });
  };

  const handleCancelReview = async () => {
    if (!cancelTargetReview) return;

    if (!isCancelReasonValid) {
      toast.error(`Please provide at least ${MIN_CANCEL_REASON_LENGTH} characters for cancellation reason`);
      return;
    }

    setCancelSubmitting(true);
    try{
      await cancelReview(cancelTargetReview.id, normalizedCancelReason);
      toast.success(`Review cancelled successfully`);
      setCancelTargetReview(null);
      setCancelReason("");
      await loadData();
    } catch (error) {
      console.error("Error cancelling review : ", error);
      toast.error(error.response?.data?.message || "Failed to cancel review");
    } finally {
      setCancelSubmitting(false);
    }
  }

  const handleDeleteReview = async () => {
  if (!deleteTargetReview) return;

  if (!canDelete("performance_management", true)) return;

  if (deleteTargetReview.status !== "Cancelled") {
    toast.error("Only cancelled reviews can be deleted");
    return;
  }

  setDeleteSubmitting(true);
  try {
    await deleteReview(deleteTargetReview.id);
    toast.success(`Review deleted successfully`);
    setDeleteTargetReview(null);
    await loadData();
  } catch (error) {
    console.error("Error deleting review:", error);
    toast.error( error.response?.data?.error || "Failed to delete review" );
  } finally {
    setDeleteSubmitting(false);
  }
};


  const loadData = async () => {
    try {
      setLoading(true);

      if (isEmployeeView) {
        const response = await getAppraisals();
        const appraisals = response.data || [];

        const cyclesMap = {};
        appraisals.forEach(appraisal => {

          const groupKey = appraisal.cycle_id ? `${appraisal.cycle_id}` : `${appraisal._id}`;
          const cycleName = appraisal.cycle_name || "One-Time Reviews";

          if (!cyclesMap[groupKey]) {
            cyclesMap[groupKey] = {
              id: groupKey,
              name: cycleName,
              department: appraisal.department || "N/A",
              deadline: appraisal.due_date,
              status: 'In Progress',
              appraisals: []
            };
          }
          cyclesMap[groupKey].appraisals.push({
            id: appraisal._id,
            employeeId: appraisal.employee_name,
            employeeName: appraisal.employee_name,
            department: appraisal.department,
            status: appraisal.status,
            cycleId: groupKey,
            template: appraisal.template_id,
            due_date: appraisal.due_date
          });
        });

        setCycles(Object.values(cyclesMap));
        setAppraisalsByCycle({});
        setExpandedCycleId(null); // All cycles collapsed by default

        const pipResponse = await getPIPs();
        const pipData = pipResponse.data || [];
        setPips(pipData);
        setExpandedPipId(null); // All PIPs collapsed by default

        const exitInterviewResponse = await getExitInterviews();
        setExitInterviews(exitInterviewResponse.data || []);
        setExpandedExitInterviewId(null);

      } else {
        const response = await getAppraisals();
        const appraisals = response.data || [];

        const cyclesMap = {};
        appraisals.forEach(appraisal => {

          const groupKey = appraisal.cycle_id ? `${appraisal.cycle_id}` : `${appraisal._id}`;
          const cycleName = appraisal.cycle_name || "One-Time Reviews";
          if (!cyclesMap[groupKey]) {
            cyclesMap[groupKey] = {
              id: groupKey,
              name: cycleName,
              department: appraisal.department || "N/A",
              deadline: appraisal.due_date,
              status: 'In Progress',
              appraisals: []
            };
          }
          cyclesMap[groupKey].appraisals.push({
            id: appraisal._id,
            employeeId: appraisal.employee_id,
            employeeName: appraisal.employee_name,
            department: appraisal.department,
            status: appraisal.status,
            cycleId: groupKey,
            template: appraisal.template_id,
            due_date: appraisal.due_date
          });
        });

        setCycles(Object.values(cyclesMap));
        setAppraisalsByCycle({});
        setExpandedCycleId(null); // All cycles collapsed by default

        const pipResponse = await getPIPs();
        const pipData = pipResponse.data || [];
        setPips(pipData);
        setExpandedPipId(null); // All PIPs collapsed by default

        const exitInterviewResponse = await getExitInterviews();
        setExitInterviews(exitInterviewResponse.data || []);
        setExpandedExitInterviewId(null);
      }
    } catch (error) {
      console.error("Error loading appraisals:", error);
      toast.error("Failed to load review data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templateData = await getTemplates();
        if (Array.isArray(templateData)) {
          setTemplates(templateData);
        } else if (templateData && Array.isArray(templateData.data)) {
          setTemplates(templateData.data);
        } else {
          setTemplates([]);
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        setTemplates([]);
      }
    }
    fetchTemplates();
  }, []);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeeData = await getEmployees(undefined, "all", {
          statuses: ["active", "exit_scheduled"],
          scope: 'performance',
        });
        // Map to simple format
        setEmployeeList(employeeData.map(emp => ({
          id: emp.employee_id,
          _id: emp._id,
          name: emp.name || "Unknown",
          department: emp.department || "N/A",
        })));
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    fetchEmployees();
  }, []);

  const toggleExpand = (cycleId) => {
    // Accordion behavior: if clicking the same cycle, collapse it; otherwise expand the new one
    setExpandedCycleId(prev => prev === cycleId ? null : cycleId);
  };

  const togglePIPExpand = (pipId) => {
    // Accordion behavior: if clicking the same PIP, collapse it; otherwise expand the new one
    setExpandedPipId(prev => prev === pipId ? null : pipId);
  };

  const toggleExitInterviewExpand = (exitInterviewId) => {
    // Accordion behavior: if clicking the same exit interview, collapse it; otherwise expand the new one
    setExpandedExitInterviewId(prev => prev === exitInterviewId ? null : exitInterviewId);
  };

  const getCycleProgress = (cycle) => {
    const appraisals = cycle.appraisals || [];
    if (appraisals.length === 0) return 0;
    const steps = isEmployeeView ? EMPLOYEE_STEPS : STEPS;
    const totalSteps = appraisals.length * (steps.length - 1);
    const completedSteps = appraisals.reduce((sum, a) => {
      const idx = steps.indexOf(a.status);
      return sum + (idx >= 0 ? idx : 0);
    }, 0);
    return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  };

  // Get unique departments from cycles
  const appraisalDepartments = useMemo(() => {
    const deps = new Set();
    cycles.forEach(cycle => {
      if (cycle.department && cycle.department !== 'N/A') {
        deps.add(cycle.department);
      }
    });
    return Array.from(deps);
  }, [cycles]);

  // Get unique departments from PIPs
  const pipDepartments = useMemo(() => {
    const deps = new Set();
    pips.forEach(pip => {
      if (pip.department && pip.department !== 'N/A') {
        deps.add(pip.department);
      }
    });
    return Array.from(deps);
  }, [pips]);

  // Get unique departments from exit interviews
  const exitInterviewDepartments = useMemo(() => {
    const deps = new Set();
    exitInterviews.forEach(ei => {
      if (ei.department && ei.department !== 'N/A') {
        deps.add(ei.department);
      }
    });
    return Array.from(deps);
  }, [exitInterviews]);

  // Filter and paginate appraisal cycles
  const filteredCycles = useMemo(() => {
    return cycles.filter(cycle => {
      // Search filter
      if (filters.appraisals.search) {
        const search = filters.appraisals.search.toLowerCase();
        const appraisals = cycle.appraisals || [];
        const hasMatchingEmployee = appraisals.some(a =>
          (a.employeeName || '').toLowerCase().includes(search)
        );
        if (
          !cycle.name.toLowerCase().includes(search) &&
          !cycle.department.toLowerCase().includes(search) &&
          !hasMatchingEmployee
        ) {
          return false;
        }
      }

      // Status filter
      if (filters.appraisals.status !== 'all') {
        const appraisals = cycle.appraisals || [];

        const hasMatchingStatus = appraisals.some(
          appraisal => appraisal.status === filters.appraisals.status
        );

        if (!hasMatchingStatus) {
          return false;
        }
      }

      // Department filter
      if (filters.appraisals.department !== 'all' && cycle.department !== filters.appraisals.department) {
        return false;
      }

      return true;
    });
  }, [cycles, filters.appraisals.search, filters.appraisals.status, filters.appraisals.department]);

  const totalAppraisalPages = Math.ceil(filteredCycles.length / pageSize);
  const paginatedCycles = filteredCycles.slice(
    (filters.appraisals.page - 1) * pageSize,
    filters.appraisals.page * pageSize
  );

  // Filter and paginate PIPs
  const filteredPips = useMemo(() => {
    return pips.filter(pip => {
      // Search filter
      if (filters.pips.search) {
        const search = filters.pips.search.toLowerCase();
        const milestoneLabels = (pip.pip_milestones || []).map((m) => m.label || "").join(" ").toLowerCase();

        if (
          !(pip.employee_name || '').toLowerCase().includes(search) &&
          !(pip.department || '').toLowerCase().includes(search) &&
          !(pip.status || '').toLowerCase().includes(search) &&
          !(pip.pip_reason || '').toLowerCase().includes(search) &&
          !milestoneLabels.includes(search)
        ) {
          return false;
        }
      }

      // Status filter
      if (filters.pips.status !== 'all' && pip.status !== filters.pips.status) {
        return false;
      }
      if (filters.pips.department !== 'all' && (pip.department || 'N/A') !== filters.pips.department) {
        return false;
      }

      return true;
    });
  }, [pips, filters.pips.search, filters.pips.status, filters.pips.department]);

  // Filter and paginate exit interviews
  const filteredExitInterviews = useMemo(() => {
    return exitInterviews.filter(ei => {
      // Search filter
      if (filters.exitInterviews.search) {
        const search = filters.exitInterviews.search.toLowerCase();
        if (
          !(ei.employee_name || '').toLowerCase().includes(search) &&
          !(ei.department || '').toLowerCase().includes(search) &&
          !(ei.status || '').toLowerCase().includes(search)
        ) {
          return false;
        }
      }

      // Status filter
      if (filters.exitInterviews.status !== 'all' && ei.status !== filters.exitInterviews.status) {
        return false;
      }

      // Department filter
      if (filters.exitInterviews.department !== 'all' && (ei.department || 'N/A') !== filters.exitInterviews.department) {
        return false;
      }

      return true;
    });
  }, [exitInterviews, filters.exitInterviews.search, filters.exitInterviews.status, filters.exitInterviews.department]);

  const totalPipPages = Math.ceil(filteredPips.length / pageSize);
  const paginatedPips = filteredPips.slice(
    (filters.pips.page - 1) * pageSize,
    filters.pips.page * pageSize
  );

  const totalExitInterviewPages = Math.ceil(filteredExitInterviews.length / pageSize);
  const paginatedExitInterviews = filteredExitInterviews.slice(
    (filters.exitInterviews.page - 1) * pageSize,
    filters.exitInterviews.page * pageSize
  );

  // Generic Pagination Component (inline)
  const renderPagination = (tab, currentPage, totalPages, totalItems) => {
    const startItem = Math.min((currentPage - 1) * pageSize + 1, totalItems);
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const generatePages = () => {
      const pages = [];
      const maxVisible = 5;

      if (totalPages <= maxVisible + 2) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        if (start > 2) pages.push('...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }

      return pages;
    };

    return (
      <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {startItem} to {endItem} of {totalItems} entries
          </div>
          <PaginationPageSize value={pageSize} onValueChange={handlePageSizeChange} />
        </div>
        <Pagination className="!justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateFilter(tab, 'page', Math.max(1, currentPage - 1))}
                className={currentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            {generatePages().map((page, index) => (
              <PaginationItem key={index}>
                {page === '...' ? (
                  <span className="px-2 text-muted-foreground">...</span>
                ) : (
                  <PaginationLink
                    isActive={page === currentPage}
                    onClick={() => updateFilter(tab, 'page', page)}
                  >
                    {page}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={() => updateFilter(tab, 'page', Math.min(totalPages, currentPage + 1))}
                className={currentPage >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };


  return (
    <>
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1
            className="text-3xl font-bold tracking-tight text-foreground"
            data-testid="active-cycles-heading"
          >
            {teamScope ? "Team reviews" : "Reviews"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {teamScope
              ? "Appraisals, improvement plans and exit interviews of the people who report to you."
              : "Track appraisal cycles, performance improvement plans and exit interviews."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={!canCreateAppraisal}
                data-testid="performance-actions-btn"
              >
                Assign
                <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {showAppraisals && (
                <DropdownMenuItem onSelect={() => setPerfAction("cycle")}>
                  <Plus className="mr-2 h-4 w-4" /> New Appraisal Cycle
                </DropdownMenuItem>
              )}
              {showAppraisals && (
                <DropdownMenuItem onSelect={() => setPerfAction("oneTime")}>
                  <Zap className="mr-2 h-4 w-4" /> One-Time Review
                </DropdownMenuItem>
              )}
              {showPips && (
                <DropdownMenuItem onSelect={() => setPerfAction("pip")}>
                  <AlertCircle className="mr-2 h-4 w-4" /> PIP
                </DropdownMenuItem>
              )}
              {/* Exit interviews are assigned by admins, not managers. */}
              {!teamScope && (
                <DropdownMenuItem onSelect={() => setPerfAction("exit")}>
                  <UserMinus className="mr-2 h-4 w-4" /> Exit Interview
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Dialogs are controlled by the dropdown above (no own triggers). */}
          <OneTimeReviewDialog
            employeeList={employeeList}
            templates={templates}
            onCreated={loadData}
            hideTrigger
            open={perfAction === "oneTime"}
            onOpenChange={(o) => setPerfAction(o ? "oneTime" : null)}
          />
          <PIPFormDialog
            employeeList={employeeList}
            templates={templates}
            onCreated={loadData}
            hideTrigger
            open={perfAction === "pip"}
            onOpenChange={(o) => setPerfAction(o ? "pip" : null)}
          />
          <AppraisalCycleDialog
            employeeList={employeeList}
            templates={templates}
            onCreated={loadData}
            hideTrigger
            open={perfAction === "cycle"}
            onOpenChange={(o) => setPerfAction(o ? "cycle" : null)}
          />
          <AssignExitInterviewDialog
            templates={templates}
            onCreated={loadData}
            hideTrigger
            open={perfAction === "exit"}
            onOpenChange={(o) => setPerfAction(o ? "exit" : null)}
          />
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
          {showAppraisals && (
            <TabsTrigger className="w-fit px-4 text-left sm:w-auto" value="appraisals">Appraisal Cycles</TabsTrigger>
          )}
          {showPips && (
            <TabsTrigger className="w-fit px-4 text-left sm:w-auto" value="pips">
              Performance Improvement Plans
            </TabsTrigger>
          )}
          {showAppraisals && (
            <TabsTrigger className="w-fit px-4 text-left sm:w-auto" value="exit-interviews">Exit Interviews</TabsTrigger>
          )}
        </TabsList>

        {/* Appraisals Tab */}
        <TabsContent value="appraisals" className="space-y-4">
          {loading ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">Loading cycles...</CardContent></Card>
          ) : cycles.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No appraisal cycles found. Create one from the Overview page.</CardContent></Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Active Appraisal Cycles</CardTitle>
                <CardDescription>Track the progress of all ongoing performance review cycles.</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by cycle name, department, or employee..."
                        value={filters.appraisals.search}
                        onChange={(e) => updateFilter('appraisals', 'search', e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select value={filters.appraisals.status} onValueChange={(value) => updateFilter('appraisals', 'status', value)}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="Self Review">Self Review</SelectItem>
                        <SelectItem value="Approval Review">Approval Review</SelectItem>
                        <SelectItem value="Employee Comment">Employee Comment</SelectItem>
                        <SelectItem value="Admin Review">Admin Review</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    {appraisalDepartments.length > 0 && (
                      <Select value={filters.appraisals.department} onValueChange={(value) => updateFilter('appraisals', 'department', value)}>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Departments</SelectItem>
                          {appraisalDepartments.map((dept) => (
                            <SelectItem key={dept} value={dept}>
                              {dept}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-gray-200 dark:border-gray-700">
                        <TableHead className="text-gray-700 dark:text-gray-300">Cycle Name</TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">Department</TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">Deadline</TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">Employees</TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">Progress</TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">Status</TableHead>
                        <TableHead className="text-right text-gray-700 dark:text-gray-300">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedCycles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            <div className="flex flex-col items-center space-y-2">
                              <Users className="h-8 w-8 text-gray-400" />
                              <p>No cycles found matching your filters</p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  updateFilter('appraisals', 'search', '');
                                  updateFilter('appraisals', 'status', 'all');
                                  updateFilter('appraisals', 'department', 'all');
                                }}
                              >
                                Clear filters
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedCycles.map((cycle) => {
                          const appraisals = cycle.appraisals || [];
                          const progress = getCycleProgress(cycle);
                          const completedCount = appraisals.filter(a => a.status === "Completed").length;
                          const pendingCount = appraisals.filter(a => a.status === "Pending").length;
                          const cancelledCount = appraisals.filter(a => a.status === "Cancelled").length;
                          const cycleStatus = appraisals.length === 0
                            ? "Pending"
                            : completedCount === appraisals.length
                              ? "Completed"
                              : cancelledCount === appraisals.length
                                ? "Cancelled"
                                : pendingCount === appraisals.length
                                  ? "Pending"
                                  : "In Progress";

                            return (
                              <TableRow
                                key={cycle.id}
                                className="border-b border-gray-200 dark:border-gray-700"
                                data-testid={`cycle-${cycle.id}`}
                              >
                                <TableCell>
                                  <div className="font-medium text-gray-900 dark:text-gray-200">
                                    {cycle.name}
                                  </div>
                                </TableCell>
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  {cycle.department}
                                </TableCell>
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  {cycle.deadline
                                    ? new Date(
                                        cycle.deadline,
                                      ).toLocaleDateString()
                                    : "No deadline"}
                                </TableCell>
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4" />
                                    <span>{appraisals.length}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-muted-foreground">
                                        {progress}%
                                      </span>
                                      <span className="text-muted-foreground">
                                        {completedCount}/{appraisals.length}
                                      </span>
                                    </div>
                                    <Progress
                                      value={progress}
                                      className="h-2"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <StatusBadge status={cycleStatus} />
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                                    {appraisals.length === 0 && (
                                      <Button
                                        size="sm"
                                        className="bg-primary hover:bg-primary/90 gap-1.5"
                                        onClick={() => {
                                          if (!canCreateAppraisal) return;
                                          navigate("/performance/dashboard");
                                        }}
                                        data-testid={`start-cycle-${cycle.id}`}
                                        disabled={!canCreateAppraisal}
                                      >
                                        <Plus className="h-3.5 w-3.5" /> Create
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                      onClick={() => toggleExpand(cycle.id)}
                                    >
                                      {expandedCycleId === cycle.id ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {renderPagination(
                    "appraisals",
                    filters.appraisals.page,
                    totalAppraisalPages,
                    filteredCycles.length,
                  )}

                  {/* Expanded Appraisals */}
                  {expandedCycleId &&
                    cycles
                      .filter((c) => c.id === expandedCycleId)
                      .map((cycle) => {
                        const appraisals = cycle.appraisals || [];
                        if (appraisals.length === 0) return null;

                  return (
                    <div key={cycle.id} className="mt-4">
                      <Separator className="mb-4" />
                      <h3 className="text-sm font-semibold text-foreground mb-3">{cycle.name} - Employee Appraisals</h3>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto thin-scrollbar">
                        <table className="w-full caption-bottom text-sm min-w-[1280px]">
                          <TableHeader>
                            <TableRow className="border-b border-gray-200 dark:border-gray-700">
                              <TableHead className="text-gray-700 dark:text-gray-300 min-w-[200px]">Employee</TableHead>
                              <TableHead className="text-gray-700 dark:text-gray-300 min-w-[140px]">Employee ID</TableHead>
                              <TableHead className="text-gray-700 dark:text-gray-300 min-w-[150px]">Department</TableHead>
                              <TableHead className="text-gray-700 dark:text-gray-300 min-w-[180px]">Template</TableHead>
                              <TableHead className="text-gray-700 dark:text-gray-300 min-w-[140px]">Status</TableHead>
                              <TableHead className="text-gray-700 dark:text-gray-300 min-w-[560px]">Progress</TableHead>
                              <TableHead className="text-right text-gray-700 dark:text-gray-300 sticky right-0 bg-card z-10 min-w-[120px] shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {appraisals.map((appraisal) => (
                              <TableRow
                                key={appraisal.id}
                                className="group border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                                data-testid={`appraisal-row-${appraisal.id}`}
                                onClick={() => {
                                  if (!canReview("performance_management", true)) return;
                                  if (isEmployeeView) {
                                    navigate(`/employee/review/${appraisal.id}`);
                                  } else {
                                    navigate(`${basePath}/review/${appraisal.id}`);
                                  }
                                }}
                              >
                                <TableCell>
                                  <div className="flex items-center space-x-3">
                                    <Avatar>
                                      <AvatarFallback className="bg-primary/10 dark:bg-primary/20 text-primary">
                                        {appraisal.employeeName?.split(' ').map(n => n[0]).join('') || 'NA'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <div className="font-medium text-gray-900 dark:text-gray-200">
                                        {appraisal.employeeName || 'N/A'}
                                      </div>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="font-mono text-gray-800 dark:text-gray-300">
                                  {typeof appraisal.employeeId === 'object'
                                    ? appraisal.employeeId?.employee_id || appraisal.employeeId?._id || 'N/A'
                                    : appraisal.employeeId || 'N/A'}
                                </TableCell>
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  {appraisal.department || 'N/A'}
                                </TableCell>
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  {appraisal.template?.name || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <StatusBadge status={appraisal.status} />
                                </TableCell>
                                <TableCell>
                                  <StepTimeline currentStatus={appraisal.status} isEmployee={isEmployeeView} />
                                </TableCell>
                                <TableCell
                                  className="text-right sticky right-0 bg-card z-10 border-gray-200 dark:border-gray-700 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary ${!canViewAppraisal ? "cursor-not-allowed" : ""
                                      }`}
                                    onClick={() => {
                                      if (!canReview("performance_management", true)) return;
                                      if (isEmployeeView) {
                                        navigate(`/employee/review/${appraisal.id}`);
                                      } else {
                                        navigate(`${basePath}/review/${appraisal.id}`);
                                      }
                                    }}
                                    title="View Details"
                                    disabled={!canViewAppraisal}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                   {canCancelReviewRecord({
                                     id: appraisal.id,
                                     reviewType: "Appraisal",
                                     status: appraisal.status,
                                   }) && (
                                     <Button
                                       variant="ghost"
                                       size="sm"
                                       className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                       onClick={() =>
                                         openCancelDialog({
                                          id: appraisal.id,
                                           reviewType: "Appraisal",
                                           status: appraisal.status,
                                           employeeName: appraisal.employeeName || "N/A",
                                         })
                                       }
                                       title="Cancel Appraisal"
                                     >
                                       <X className="h-4 w-4" />
                                     </Button>
                                   )}
                                   {canDeleteReviewRecord({
                                    id: appraisal.id,
                                    reviewType: "Appraisal",
                                    status: appraisal.status,
                                  }) && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() =>
                                          setDeleteTargetReview({
                                            id: appraisal.id,
                                            reviewType: "Appraisal",
                                            status: appraisal.status,
                                            employeeName: appraisal.employeeName || "N/A",
                                            cycleId: appraisal.cycleMongoId,
                                          })}
                                        title="Delete Appraisal"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* PIPs Tab */}
        <TabsContent value="pips" className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading PIPs...
              </CardContent>
            </Card>
          ) : pips.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No active Performance Improvement Plans found.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Active Performance Improvement Plans</CardTitle>
                <CardDescription>
                  Monitor ongoing PIPs and their progress.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by employee name, department, or status..."
                        value={filters.pips.search}
                        onChange={(e) =>
                          updateFilter("pips", "search", e.target.value)
                        }
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={filters.pips.status}
                      onValueChange={(value) =>
                        updateFilter("pips", "status", value)
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="Draft">Pending</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    {pipDepartments.length > 0 && (
                      <Select
                        value={filters.pips.department}
                        onValueChange={(value) =>
                          updateFilter("pips", "department", value)
                        }
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Departments</SelectItem>
                          {pipDepartments.map((dept) => (
                            <SelectItem key={dept} value={dept}>
                              {dept}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-gray-200 dark:border-gray-700">
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Employee
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Department
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Duration
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Milestones
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Progress
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Status
                        </TableHead>
                        <TableHead className="text-right text-gray-700 dark:text-gray-300">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedPips.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="text-center py-8 text-muted-foreground"
                          >
                            <div className="flex flex-col items-center space-y-2">
                              <Users className="h-8 w-8 text-gray-400" />
                              <p>No PIPs found matching your filters</p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  updateFilter("pips", "search", "");
                                  updateFilter("pips", "status", "all");
                                  updateFilter("pips", "department", "all");
                                }}
                              >
                                Clear filters
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedPips.map((pip) => {
                          const isCompleted = pip.status === "Completed";
                          const completedMilestones = (
                            pip.pip_milestones || []
                          ).filter((m) => m.completed === true).length;
                          const totalMilestones = (pip.pip_milestones || [])
                            .length;
                          const milestonePct =
                            totalMilestones > 0
                              ? Math.round(
                                  (completedMilestones / totalMilestones) * 100,
                                )
                              : 0;

                          return (
                            <TableRow
                              key={pip._id}
                              className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                              data-testid={`pip-${pip._id}`}
                              onClick={() => {
                                if (!canReview("performance_management", true))
                                  return;
                                navigate(`${basePath}/pip/${pip._id}`);
                              }}
                            >
                              <TableCell>
                                <div className="font-medium text-gray-900 dark:text-gray-200">
                                  {pip.employee_name}
                                </div>
                              </TableCell>
                              <TableCell className="text-gray-800 dark:text-gray-300">
                                {pip.department || "N/A"}
                              </TableCell>
                              <TableCell className="text-gray-800 dark:text-gray-300">
                                {new Date(
                                  pip.pip_start_date,
                                ).toLocaleDateString()}{" "}
                                —{" "}
                                {new Date(
                                  pip.pip_end_date,
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="text-gray-800 dark:text-gray-300">
                                {completedMilestones}/{totalMilestones}
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">
                                      {milestonePct}%
                                    </span>
                                    <span className="text-muted-foreground">
                                      {completedMilestones}/{totalMilestones}
                                    </span>
                                  </div>
                                  <Progress
                                    value={milestonePct}
                                    className="h-2"
                                  />
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${
                                    pip.status === "Pending" ||
                                    pip.status === "Draft"
                                      ? "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30"
                                      : pip.status === "Completed"
                                        ? "bg-primary text-primary-foreground border-primary dark:bg-primary dark:text-white dark:border-primary"
                                        : pip.status === "Cancelled"
                                          ? "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800"
                                          : "bg-muted text-muted-foreground border-muted-foreground/20"
                                  }`}
                                >
                                  {pip.status === "Draft"
                                    ? "Pending"
                                    : pip.status}
                                </Badge>
                              </TableCell>
                              <TableCell
                                className="text-right"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-end space-x-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className={`h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary ${
                                      !canViewPIP
                                        ? "opacity-70 cursor-not-allowed"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      if (
                                        !canReview(
                                          "performance_management",
                                          true,
                                        )
                                      )
                                        return;
                                      navigate(`${basePath}/pip/${pip._id}`);
                                    }}
                                    title="View Details"
                                    disabled={!canViewPIP}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>

                                    {canCancelReviewRecord({
                                     id: pip._id,
                                     reviewType: "PIP",
                                     status: pip.status,
                                   }) && (
                                     <Button
                                       size="sm"
                                       variant="ghost"
                                       className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                       onClick={() =>
                                         openCancelDialog({
                                           id: pip._id,
                                           reviewType: "PIP",
                                           status: pip.status,
                                         })
                                       }
                                       title="Cancel PIP"
                                     >
                                       <X className="h-4 w-4" />
                                     </Button>
                                   )}

                                  {canDeleteReviewRecord({
                                    id: pip._id,
                                    reviewType: "PIP",
                                    status: pip.status,
                                  }) && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() =>
                                          setDeleteTargetReview({
                                            id: pip._id,
                                            reviewType: "PIP",
                                            status: pip.status,
                                          })}
                                        title="Delete PIP"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}

                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                    onClick={() => togglePIPExpand(pip._id)}
                                    title="Expand/Collapse Milestones"
                                  >
                                    {expandedPipId === pip._id ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {renderPagination(
                  "pips",
                  filters.pips.page,
                  totalPipPages,
                  filteredPips.length,
                )}

                {/* Expanded Milestones */}
                {expandedPipId &&
                  pips
                    .filter((pip) => pip._id === expandedPipId)
                    .map((pip) => {
                      if (
                        !pip.pip_milestones ||
                        pip.pip_milestones.length === 0
                      )
                        return null;

                      return (
                        <div key={pip._id} className="mt-4">
                          <Separator className="mb-4" />
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-foreground">
                              {pip.employee_name} - Milestones
                            </h3>
                            <StatusBadge status={pip.status} />
                          </div>
                          {pip.pip_reason && (
                            <p className="text-sm text-muted-foreground mb-4">
                              {pip.pip_reason}
                            </p>
                          )}
                          <div className="border border-gray-200 dark:border-gray-700 rounded-md">
                            <Table>
                              <TableHeader>
                                <TableRow className="border-b border-gray-200 dark:border-gray-700">
                                  <TableHead className="text-gray-700 dark:text-gray-300 w-12">
                                    #
                                  </TableHead>
                                  <TableHead className="text-gray-700 dark:text-gray-300">
                                    Milestone
                                  </TableHead>
                                  <TableHead className="text-gray-700 dark:text-gray-300">
                                    Target Date
                                  </TableHead>
                                  <TableHead className="text-gray-700 dark:text-gray-300">
                                    Status
                                  </TableHead>
                                  <TableHead className="text-gray-700 dark:text-gray-300">
                                    Notes
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pip.pip_milestones.map((ms, idx) => {
                                  const isDone =
                                    ms.completed === true ||
                                    ms.status === "Completed";
                                  return (
                                    <TableRow
                                      key={ms.milestone_id || ms.id || idx}
                                      className="border-b border-gray-200 dark:border-gray-700"
                                    >
                                      <TableCell className="text-gray-800 dark:text-gray-300">
                                        <div
                                          className={`h-7 w-7 rounded-full flex items-center justify-center ${isDone ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                                        >
                                          {isDone ? (
                                            <CheckCircle2 className="h-4 w-4" />
                                          ) : (
                                            <span className="text-xs font-semibold">
                                              {idx + 1}
                                            </span>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="font-medium text-gray-900 dark:text-gray-200">
                                          {ms.label}
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-gray-800 dark:text-gray-300">
                                        {new Date(ms.date).toLocaleDateString()}
                                      </TableCell>
                                      <TableCell>
                                        <Badge
                                          variant="outline"
                                          className={`text-[10px] ${
                                            isDone
                                              ? "bg-primary text-primary-foreground border-primary dark:bg-primary dark:text-white dark:border-primary"
                                              : "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30"
                                          }`}
                                        >
                                          {isDone ? "Completed" : "Pending"}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-gray-800 dark:text-gray-300">
                                        {ms.notes ? (
                                          <p
                                            className="text-sm italic max-w-xs truncate"
                                            title={ms.notes}
                                          >
                                            "{ms.notes}"
                                          </p>
                                        ) : (
                                          <span className="text-muted-foreground">
                                            —
                                          </span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      );
                    })}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Exit Interviews Tab */}
        <TabsContent value="exit-interviews" className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading exit interviews...
              </CardContent>
            </Card>
          ) : exitInterviews.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No exit interviews found.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Exit Interviews</CardTitle>
                <CardDescription>
                  Track and manage employee exit interviews.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by employee name, department, or status..."
                        value={filters.exitInterviews.search}
                        onChange={(e) =>
                          updateFilter(
                            "exitInterviews",
                            "search",
                            e.target.value,
                          )
                        }
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={filters.exitInterviews.status}
                      onValueChange={(value) =>
                        updateFilter("exitInterviews", "status", value)
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Approval Review">
                          Approval Review
                        </SelectItem>
                        <SelectItem value="Admin Review">
                          Admin Review
                        </SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    {exitInterviewDepartments.length > 0 && (
                      <Select
                        value={filters.exitInterviews.department}
                        onValueChange={(value) =>
                          updateFilter("exitInterviews", "department", value)
                        }
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Departments</SelectItem>
                          {exitInterviewDepartments.map((dept) => (
                            <SelectItem key={dept} value={dept}>
                              {dept}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-gray-200 dark:border-gray-700">
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Employee
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Department
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Status
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Date
                        </TableHead>
                        <TableHead className="text-right text-gray-700 dark:text-gray-300">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedExitInterviews.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="text-center py-8 text-muted-foreground"
                          >
                            <div className="flex flex-col items-center space-y-2">
                              <Users className="h-8 w-8 text-gray-400" />
                              <p>
                                No exit interviews found matching your filters
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  updateFilter("exitInterviews", "search", "");
                                  updateFilter(
                                    "exitInterviews",
                                    "status",
                                    "all",
                                  );
                                  updateFilter(
                                    "exitInterviews",
                                    "department",
                                    "all",
                                  );
                                }}
                              >
                                Clear filters
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedExitInterviews.map((ei) => {
                          return (
                            <TableRow
                              key={ei._id}
                              className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                              data-testid={`exit-interview-${ei._id}`}
                              onClick={() => {
                                if (!canReview("performance_management", true))
                                  return;
                                navigate(`${basePath}/exit-interview/${ei._id}`);
                              }}
                            >
                              <TableCell>
                                <div className="font-medium text-gray-900 dark:text-gray-200">
                                  {ei.employee_name}
                                </div>
                              </TableCell>
                              <TableCell className="text-gray-800 dark:text-gray-300">
                                {ei.department || "N/A"}
                              </TableCell>
                              <TableCell>
                                <StatusBadge status={ei.status} />
                              </TableCell>
                              <TableCell className="text-gray-800 dark:text-gray-300">
                                {ei.exit_interview?.submitted_at
                                  ? new Date(
                                      ei.exit_interview.submitted_at,
                                    ).toLocaleDateString()
                                  : ei.due_date
                                    ? new Date(ei.due_date).toLocaleDateString()
                                    : "N/A"}
                              </TableCell>
                              <TableCell
                                className="text-right"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-end space-x-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className={`h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary ${
                                      !canViewExitInterview
                                        ? "opacity-70 cursor-not-allowed"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      if (
                                        !canReview(
                                          "performance_management",
                                          true,
                                        )
                                      )
                                        return;
                                      navigate(
                                        `${basePath}/exit-interview/${ei._id}`,
                                      );
                                    }}
                                    title="View Details"
                                    disabled={!canViewExitInterview}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>

                                  {canCancelReviewRecord({
                                    id: ei._id,
                                    reviewType: "Exit Interview",
                                    status: ei.status,
                                  }) && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                      onClick={() =>
                                        openCancelDialog({
                                          id: ei._id,
                                          reviewType: "Exit Interview",
                                          status: ei.status,
                                          employeeName: ei.employee_name || "N/A",
                                        })
                                      }
                                      title="Cancel Exit Interview"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canDeleteReviewRecord({
                                    id: ei._id,
                                    reviewType: "Exit Interview",
                                    status: ei.status,
                                  }) && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                      onClick={() =>
                                        setDeleteTargetReview({
                                          id: ei._id,
                                          reviewType: "Exit Interview",
                                          status: ei.status,
                                          employeeName: ei.employee_name || "N/A",
                                        })
                                      }
                                      title="Delete Exit Interview"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}

                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                    onClick={() =>
                                      toggleExitInterviewExpand(ei._id)
                                    }
                                    title="Expand/Collapse Details"
                                  >
                                    {expandedExitInterviewId === ei._id ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {renderPagination(
                  "exitInterviews",
                  filters.exitInterviews.page,
                  totalExitInterviewPages,
                  filteredExitInterviews.length,
                )}

                {/* Expanded Exit Interview Details */}
                {expandedExitInterviewId &&
                  exitInterviews
                    .filter((ei) => ei._id === expandedExitInterviewId)
                    .map((ei) => {
                      return (
                        <div key={ei._id} className="mt-4">
                          <Separator className="mb-4" />
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-foreground">
                              {ei.employee_name} - Exit Interview Details
                            </h3>
                            <StatusBadge status={ei.status} />
                          </div>
                          <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                            <div className="grid grid-cols-1 md:grid-cols-2">
                              <div className="p-4 border-b border-gray-200 dark:border-gray-700 md:border-r">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Department
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                  {ei.department || "N/A"}
                                </p>
                              </div>
                              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Last Working Day
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                  {ei.employee_id?.last_working_date
                                    ? new Date(
                                        ei.employee_id.last_working_date,
                                      ).toLocaleDateString()
                                    : "N/A"}
                                </p>
                              </div>
                              <div className="p-4 border-b border-gray-200 dark:border-gray-700 md:border-b-0 md:border-r">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Submitted On
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                  {ei.exit_interview?.submitted_at
                                    ? new Date(
                                        ei.exit_interview.submitted_at,
                                      ).toLocaleDateString()
                                    : "Not submitted"}
                                </p>
                              </div>
                              <div className="p-4">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Template
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                  {ei.template_id?.name ||
                                    ei.exit_interview_template_name ||
                                    "N/A"}
                                </p>
                              </div>
                            </div>
                            {ei.exit_interview?.selected_reasons?.length >
                              0 && (
                              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Reason for Leaving
                                </p>
                                <p className="text-sm text-gray-800 dark:text-gray-300">
                                  {ei.exit_interview.selected_reasons.join(
                                    ", ",
                                  )}
                                </p>
                              </div>
                            )}
                            {ei.exit_interview?.overall_comment && (
                              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Feedback
                                </p>
                                <p className="text-sm text-gray-800 dark:text-gray-300">
                                  {ei.exit_interview.overall_comment}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>


      <Dialog
        open={Boolean(cancelTargetReview)}
        onOpenChange={(open) => {
          if (!open && !cancelSubmitting) {
            setCancelTargetReview(null);
            setCancelReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Cancel {cancelTargetReview?.reviewType || "Review"}
            </DialogTitle>
            <DialogDescription>
              This will mark the review as cancelled. The record will remain in
              history.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <p className="text-sm">
              <span className="font-medium">
                Please provide a valid cancellation reason
              </span>
            </p>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation"
              className="min-h-[90px] focus-visible:ring-red-500"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelTargetReview(null)}
              className="focus-visible:ring-red-500"
              disabled={cancelSubmitting}
            >
              Keep Review
            </Button>
            <Button
              variant="destructive"
              className="focus-visible:ring-red-500"
              onClick={handleCancelReview}
              disabled={cancelSubmitting || !isCancelReasonValid}
            >
              {cancelSubmitting ? "Cancelling..." : "Cancel Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteTargetReview)} onOpenChange={(open) => {
        if (!open && !deleteSubmitting) {
          setDeleteTargetReview(null);
        }
      }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Delete {deleteTargetReview?.reviewType || "Review"}
            </DialogTitle>
            <DialogDescription>
              This will permanently delete the cancelled record. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTargetReview(null)}
              className="focus-visible:ring-red-500"
              disabled={deleteSubmitting}
            >
              Keep Review
            </Button>
            <Button
              variant="destructive"
              className="focus-visible:ring-red-500"
              onClick={handleDeleteReview}
              disabled={deleteSubmitting}
            >
              {deleteSubmitting ? "Deleting..." : "Delete Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </>
  );
};

export default MyAppraisalsPage;
